import type { UndefinedOnPartialDeep } from 'type-fest';

import ListView, { ListViewItem } from '@ingka/list-view';
import SSRIcon from '@ingka/ssr-icon';
import clock from '@ingka/ssr-icon/paths/clock';
import deliveryTruck from '@ingka/ssr-icon/paths/delivery-truck';
import warehouse from '@ingka/ssr-icon/paths/warehouse';
import wheelchair from '@ingka/ssr-icon/paths/wheelchair';
import { selectDeliveryContact } from '@repo/api-adapters/selectors/customer.selectors';
import { assetTypes } from '@repo/types/asset';
import { type Service, isPickupServiceType } from '@repo/types/service';

import { Feature } from '@/components/feature';

import { useAsset, useAssetParams } from '@/hooks/asset.hooks';
import {
    getDeliveryType,
    getEstimatedServiceArrivalTime,
    getOrderStatus,
    isFullServeHandoutDelivery,
} from '@/utils/services.utils';

import { ArticleDeliveryAddressIcon } from './article-delivery-address-icon';
import { ArticleDeliveryAddressText } from './article-delivery-address-text';
import ArticleDeliveryFulfillingUnit from './article-delivery-fulfilling-unit/article-delivery-fulfilling-unit.component';
import { ArticleDeliveryStatusIcon } from './article-delivery-status-icon/article-delivery-status-icon.component';
import { ArticleDeliveryStatusText } from './article-delivery-status-text';
import { ArticleDeliveryTimeText } from './article-delivery-time-text';
import { ArticleWheelChairAccessibilityText } from './article-delivery-wheelchair-accessibility';

import parentStyles from '../styles.module.scss';
import styles from './styles.module.scss';

interface Props {
    service: UndefinedOnPartialDeep<Service>;
}

export const ArticleDeliveryView = ({ service }: Props) => {
    const { assetType, assetId } = useAssetParams();
    const { data } = useAsset(assetType, assetId);
    const deliveryContact = selectDeliveryContact(data);

    const { serviceType, statusCode, timeWindow, serviceProviderName, isDelivery } = service;

    const deliveryType = getDeliveryType(serviceType);
    const orderStatus = getOrderStatus(statusCode);
    const estimatedDeliveryTime = getEstimatedServiceArrivalTime(timeWindow, isDelivery);
    const isOrderAsset = assetType === assetTypes.order;
    const fulfillmentUnit = timeWindow?.fulfillmentUnit;
    const isFsHandoutDelivery = isFullServeHandoutDelivery(service);

    const renderDeliveryForm = () => {
        if (isFsHandoutDelivery) {
            return null;
        }

        if (isPickupServiceType(serviceType)) {
            return (
                <>
                    <div
                        className={`${parentStyles.article_view_box} ${parentStyles.article_list_container}`}
                        data-testid="article-delivery-view-delivery-time"
                    >
                        <SSRIcon paths={clock} />
                        <ArticleDeliveryTimeText service={service} estimatedDeliveryTime={estimatedDeliveryTime} />
                    </div>
                    <div
                        className={`${parentStyles.article_view_box} ${parentStyles.article_list_container}`}
                        data-testid="article-delivery-view-delivery-accessibility"
                    >
                        <SSRIcon paths={wheelchair} />
                        <ArticleWheelChairAccessibilityText capabilities={service.capabilities} />
                    </div>
                </>
            );
        }

        return (
            <div
                className={`${parentStyles.article_view_box} ${parentStyles.article_list_container}`}
                data-testid="article-delivery-view-delivery-provider"
            >
                <SSRIcon paths={deliveryTruck} />
                {serviceProviderName}
            </div>
        );
    };

    return (
        <Feature configPath={['articleDeliveryView.enable']}>
            <div className={styles.article_delivery_view_container} data-testid="article-delivery-view">
                <ListView id="article-delivery-header" size="medium">
                    <ListViewItem
                        title={deliveryType}
                        inset={true}
                        emphasised
                        description={
                            isOrderAsset ? (
                                <span data-testid="article-delivery-view-delivery-estimated-delivery-time">
                                    {estimatedDeliveryTime}
                                </span>
                            ) : undefined
                        }
                        data-testid="article-delivery-view-header"
                    />
                </ListView>
                {isOrderAsset && (
                    <>
                        <div
                            className={`${parentStyles.article_view_box} ${parentStyles.article_list_container}`}
                            data-testid="article-delivery-view-delivery-status"
                        >
                            <ArticleDeliveryStatusIcon orderStatus={orderStatus} />
                            <ArticleDeliveryStatusText service={service} />
                        </div>
                        <div
                            className={`${parentStyles.article_view_box} ${parentStyles.article_list_container}`}
                            data-testid="article-delivery-view-delivery-fulfillment-unit"
                        >
                            <SSRIcon paths={warehouse} />
                            <ArticleDeliveryFulfillingUnit fulfillmentUnit={fulfillmentUnit} />
                        </div>
                    </>
                )}
                {!isFsHandoutDelivery && (
                    <div
                        className={`${parentStyles.article_view_box} ${parentStyles.article_list_container}`}
                        data-testid="article-delivery-view-delivery-address"
                    >
                        <ArticleDeliveryAddressIcon serviceType={serviceType} />
                        <ArticleDeliveryAddressText service={service} deliveryContact={deliveryContact} />
                    </div>
                )}
                {renderDeliveryForm()}
            </div>
        </Feature>
    );
};
